/**
 * Circuit Breaker - v13 Section 6.11
 *
 * Implements circuit breaker pattern for LLM provider resilience.
 */
/**
 * Circuit breaker states
 */
export var CircuitState;
(function (CircuitState) {
    CircuitState["CLOSED"] = "closed";
    CircuitState["OPEN"] = "open";
    CircuitState["HALF_OPEN"] = "half_open";
})(CircuitState || (CircuitState = {}));
/**
 * Default circuit breaker config
 */
export const DEFAULT_CIRCUIT_BREAKER_CONFIG = {
    failureThreshold: 5,
    resetTimeoutMs: 30000, // 30 seconds
    halfOpenRequests: 2,
};
/**
 * CircuitBreaker - Protects against cascading failures
 */
export class CircuitBreaker {
    constructor(config) {
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.totalRequests = 0;
        this.halfOpenRequestsInFlight = 0;
        this.config = { ...DEFAULT_CIRCUIT_BREAKER_CONFIG, ...config };
    }
    /**
     * Get current state
     */
    getState() {
        // Check if we should transition from open to half-open
        if (this.state === CircuitState.OPEN && this.openedAt) {
            const elapsed = Date.now() - this.openedAt;
            if (elapsed >= this.config.resetTimeoutMs) {
                this.state = CircuitState.HALF_OPEN;
                this.halfOpenRequestsInFlight = 0;
            }
        }
        return this.state;
    }
    /**
     * Check if request is allowed
     */
    canRequest() {
        const state = this.getState();
        if (state === CircuitState.CLOSED) {
            return true;
        }
        if (state === CircuitState.HALF_OPEN) {
            return this.halfOpenRequestsInFlight < this.config.halfOpenRequests;
        }
        return false;
    }
    /**
     * Mark start of a request (for half-open limiting)
     */
    startRequest() {
        if (this.getState() === CircuitState.HALF_OPEN) {
            this.halfOpenRequestsInFlight++;
        }
    }
    /**
     * Record a successful request
     */
    recordSuccess() {
        this.totalRequests++;
        this.successCount++;
        this.lastSuccessTime = Date.now();
        this.failureCount = 0;
        if (this.state === CircuitState.HALF_OPEN) {
            this.halfOpenRequestsInFlight = Math.max(0, this.halfOpenRequestsInFlight - 1);
        }
        // Close circuit on success in half-open
        if (this.state === CircuitState.HALF_OPEN) {
            this.state = CircuitState.CLOSED;
            this.openedAt = undefined;
        }
    }
    /**
     * Record a failed request
     */
    recordFailure() {
        this.totalRequests++;
        this.failureCount++;
        this.lastFailureTime = Date.now();
        if (this.state === CircuitState.HALF_OPEN) {
            this.halfOpenRequestsInFlight = Math.max(0, this.halfOpenRequestsInFlight - 1);
        }
        // Open circuit if threshold reached
        if (this.failureCount >= this.config.failureThreshold) {
            this.state = CircuitState.OPEN;
            this.openedAt = Date.now();
        }
        // Re-open on failure in half-open
        if (this.state === CircuitState.HALF_OPEN) {
            this.state = CircuitState.OPEN;
            this.openedAt = Date.now();
        }
    }
    /**
     * Get failure count
     */
    getFailureCount() {
        return this.failureCount;
    }
    /**
     * Get statistics
     */
    getStats() {
        return {
            totalRequests: this.totalRequests,
            successCount: this.successCount,
            failureCount: this.failureCount,
            state: this.getState(),
            lastFailureTime: this.lastFailureTime,
            lastSuccessTime: this.lastSuccessTime,
        };
    }
    /**
     * Execute a function with circuit breaker protection
     */
    async execute(fn) {
        if (!this.canRequest()) {
            throw new Error('Circuit breaker is open');
        }
        this.startRequest();
        try {
            const result = await fn();
            this.recordSuccess();
            return result;
        }
        catch (error) {
            this.recordFailure();
            throw error;
        }
    }
    /**
     * Reset the circuit breaker
     */
    reset() {
        this.state = CircuitState.CLOSED;
        this.failureCount = 0;
        this.openedAt = undefined;
        this.halfOpenRequestsInFlight = 0;
    }
}
